/**
 * Locations — the workspace's public venue catalog
 * (workspaces/{orgId}/locations). A domain-root shared primitive: every
 * event's header resolves its venue against this catalog, and the admin
 * location-CRUD surface (locations/locations.state.ts) builds its own
 * page/form view-models and write-intent channel on top of this same read
 * state rather than declaring a second one. Pure state; locations.sync.ts
 * (web-side, a boot reconciler mirroring workspace.sync.ts) is the sole
 * writer.
 */
import { state } from "@mhmo91/schmancy/state";
import type { Location } from "@momo/types";

export type LocationWithId = Location.Location & { id: string };

// keepOnClear: the venue catalog is public and identity-free — a sign-in or
// sign-out elsewhere in the same tab must not wipe it. Its sole writer is a
// live Firestore snapshot listener (locations.sync.ts) that only re-emits
// when the catalog itself changes, so a clearStates() reset would strand it
// at [] with nothing to repopulate it.
export const locationsState = state<readonly LocationWithId[]>(
	"storefront/locations",
).memory([], { keepOnClear: true });

/**
 * Locations data status — distinguishes "waiting for first snapshot" from
 * "snapshot arrived and is empty" so the admin locations list can show a
 * loading affordance instead of a premature empty state. "denied" is a
 * Firestore permission rejection; "error" is everything else — network,
 * offline, a transient listener fault. Mirrors events.state.ts's
 * eventsStatus / members.state.ts's membersStatus. keepOnClear: true for the
 * same reason as locationsState above — its sole writer is a live listener
 * that only re-emits on a real catalog change, so a clearStates() reset would
 * strand it at "loading" while locationsState itself still holds the catalog.
 */
export const locationsStatus = state<"loading" | "ready" | "error" | "denied">(
	"storefront/locationsStatus",
).memory("loading", { keepOnClear: true });
